import type { IPairing, IPairingLogicModifier, IRegLimitEvaluation } from "@vmosolutions/oc-interfaces";
import { Grid, Checkbox } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { convertMilli2DaysHrsMin } from "src/utils/index";
import type { ILMGroupChipProps } from "../../../LogicModifiers/LMGroupChip";
import { LMGroupChip } from "../../../LogicModifiers/LMGroupChip";
import type { IPairingEditorEventGroup } from "../PairingEventTable";
import { DutyPeriodLimits } from "./DutyPeriodLimits";
import { useCallback, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import type { IRootState } from "src/redux/reducers/CombinedReducers";

type IDutyPeriodSummary = {
  id: string;
  eventGroupIndex: number;
  eventGroup: IPairingEditorEventGroup;
  logicModifiersProps: ILMGroupChipProps;
  bulkRemoveMode: boolean;
  checkedEvents: string[];
  setCheckedEvents: React.Dispatch<React.SetStateAction<string[]>>;
  flightDutyPeriodDisplay: boolean;
  regLimits: IRegLimitEvaluation | null;
  regLimitsLoading: boolean;
  /** Index into the reg-limit `dutyGroups` array (duty periods only, excludes rest periods). */
  dutyPeriodIndex: number;
  /** True when the displayed limits were evaluated against a pairing that differs from the live editor state
   *  (edited since the last fetch/recalculate), so they may be stale/misaligned. */
  regLimitsStale: boolean;
}

export const DutyPeriodSummary = (props: IDutyPeriodSummary): JSX.Element => {
  const {
    eventGroupIndex,
    eventGroup,
    logicModifiersProps,
    bulkRemoveMode,
    checkedEvents,
    setCheckedEvents,
    flightDutyPeriodDisplay,
    regLimits,
    regLimitsLoading,
    dutyPeriodIndex,
    regLimitsStale,
  } = props;
  const [checked, setChecked] = useState<boolean>(false);
  const storedPairing = useSelector(
    (state: IRootState): IPairing | undefined => state.crewSchedule.pairingEditors[props.id]
  );

  useEffect(() => {
    if (checkedEvents.length === 0) {
      setChecked(false);
      return;
    }
    const sortedEvents = eventGroup.sortedEvents ?? [];
    let allChecked = true;
    for (let n = 0; n < sortedEvents.length; n++) {
      if (!checkedEvents.includes(sortedEvents[n]._id)) {
        allChecked = false;
        break;
      }
    }
    setChecked(allChecked);
  }, [checkedEvents, eventGroup.sortedEvents]);

  const handleCheck = useCallback(() => {
    const tempCheckedEvents = [...checkedEvents];
    const sortedEvents = eventGroup.sortedEvents ?? [];
    if (!checked) {
      sortedEvents.forEach((event) => {
        if (!tempCheckedEvents.includes(event._id)) {
          tempCheckedEvents.push(event._id);
        }
      });
    } else {
      sortedEvents.forEach((event) => {
        if (tempCheckedEvents.includes(event._id)) {
          tempCheckedEvents.splice(tempCheckedEvents.indexOf(event._id), 1);
        }
      });
      setChecked(false);
    }
    setCheckedEvents(tempCheckedEvents);
  }, [checked, checkedEvents, eventGroup.sortedEvents, setCheckedEvents]);

  if (eventGroup.sortedEvents != null && eventGroup.sortedEvents.length > 0 && storedPairing != null) {
    const { checkInOutOverrides } = storedPairing;

    // CHECKING TIME to display as well as allowable minMax values for possible override.
    const firstEvent = eventGroup.sortedEvents[0].event;
    const checkInOverride = checkInOutOverrides?.find((o) => o.eventGroup === eventGroupIndex && o.inout);
    const checkInTime = checkInOverride?.time ?? eventGroup.start.valueOf();

    // CHECKOUT TIME to display.
    const lastEvent = eventGroup.sortedEvents.slice(-1).pop()?.event;
    const checkOutOverride = checkInOutOverrides?.find((o) => o.eventGroup === eventGroupIndex && !o.inout);
    const checkOutTime = checkOutOverride?.time ?? eventGroup.end.valueOf();

    // Only flight-like events carry origin/destination; other event kinds display blank rather than "undefined".
    const firstOrigin = "origin" in firstEvent ? firstEvent.origin : "";
    const lastDestination = lastEvent != null && "destination" in lastEvent ? lastEvent.destination : "";

    // LOGIC MODIFIERS
    const logicModifierInRange = (logicModifiers: IPairingLogicModifier): boolean =>
      logicModifiers.dutyTime != null &&
      logicModifiers.dutyTime >= checkInTime &&
      logicModifiers.dutyTime <= checkOutTime;
    const logicModifiersInDutyPeriod = logicModifiersProps.logicModifiers.filter(
      (lms: IPairingLogicModifier) =>
        lms.dutyTime != null && lms.dutyTime >= checkInTime && lms.dutyTime <= checkOutTime
    );

    // Highlight this duty period if it carries the pairing's most-limiting regs limit constraint AND that
    // constraint is exceeded (negative buffer).
    // Only while duty-limit checks are toggled on.
    const isMostLimitingExceeded =
      flightDutyPeriodDisplay &&
      !regLimitsStale &&
      regLimits?.mostLimiting?.dutyGroupIndex === dutyPeriodIndex &&
      regLimits.mostLimiting.bufferHours < 0;

    return (
      <Grid
        container
        className="duty_period_summary"
        spacing={0}
        alignItems="center"
        sx={
          isMostLimitingExceeded
            ? {
                borderLeft: "3px solid",
                borderColor: "error.main",
                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.25),
                pl: 0.5,
              }
            : undefined
        }
      >
        <Grid item xs={11.5}>
          <Grid container direction={"column"}>
            <Grid item>
              <span style={{ fontWeight: 500 }}>Duty Period: </span>
              {`${firstOrigin} to ${lastDestination} Duration - ${convertMilli2DaysHrsMin(
                checkOutTime.valueOf() - checkInTime.valueOf()
              )} `}
              {flightDutyPeriodDisplay && (regLimits != null || regLimitsLoading) && (
                <DutyPeriodLimits
                  dutyPeriodIndex={dutyPeriodIndex}
                  regLimits={regLimits}
                  loading={regLimitsLoading}
                  storedPairing={storedPairing}
                  stale={regLimitsStale}
                />
              )}
            </Grid>

            <Grid item>
              {logicModifiersInDutyPeriod.length > 0 && (
                <LMGroupChip {...{ ...logicModifiersProps, logicModifierInRange }} />
              )}
            </Grid>
          </Grid>
        </Grid>
        {bulkRemoveMode && (
          <Grid item xs={0.5}>
            <Checkbox
              size="small"
              onChange={handleCheck}
              checked={checked}
              disableRipple={true}
              sx={{ pt: 0, pb: 0, "& .MuiSvgIcon-root": { fontSize: 14 } }}
            />
          </Grid>
        )}
      </Grid>
    );
  }
  return <div className="duty_period_summary">Please Add events to generate an event summary.</div>;
};
